import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Loader2 } from "lucide-react";

interface ExitTestWarningDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isExiting?: boolean;
}

export const ExitTestWarningDialog = ({
  open,
  onConfirm,
  onCancel,
  isExiting = false,
}: ExitTestWarningDialogProps) => {
  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && !isExiting && onCancel()}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <AlertDialogTitle className="text-xl">Exit Test?</AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-4 pt-2">
              <p className="text-muted-foreground">
                Are you sure you want to exit? Please review the consequences:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">⚠️</span>
                  <span>Your responses up to the last saved answer will be preserved</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5">⚠️</span>
                  <span>This test will <strong>NOT</strong> be marked or evaluated</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5">⚠️</span>
                  <span>This test will be counted as <strong>abandoned</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5">⚠️</span>
                  <span><strong>You will not be able to take this test again</strong></span>
                </li>
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel 
            onClick={onCancel} 
            disabled={isExiting}
            className="mt-0"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isExiting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isExiting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exiting...
              </>
            ) : (
              "Exit Test"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
